import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Calendar, ChevronRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Feb 2026 — Booking Availability banner.
 *
 * Renders a small notice on the admin dashboard when one or more
 * `booking_availability_rules` are currently active OR upcoming
 * within the next 30 days. Silent when nothing's happening.
 *
 * Read-only. No mutations. Fail-soft: any API error hides the banner.
 */
export default function BookingAvailabilityBanner() {
  const [summary, setSummary] = useState({ active: [], upcoming: [] });

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/booking-availability/summary")
      .then(({ data }) => { if (!cancelled) setSummary(data || { active: [], upcoming: [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const active   = summary.active   || [];
  const upcoming = (summary.upcoming || []).slice(0, 3);

  if (active.length === 0 && upcoming.length === 0) return null;

  const codeChip = (c) => (
    <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/70 border border-current/20 text-[10px] font-mono">{c}</span>
  );

  return (
    <div
      className="mb-6 rounded-2xl border border-amber-300 bg-amber-50/70 p-4"
      data-testid="admin-dashboard-booking-availability-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-200/60 flex items-center justify-center shrink-0">
          <Calendar className="w-4 h-4 text-amber-800" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-2 flex-wrap">
            Booking availability rules
            {active.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-600 text-white">
                <AlertTriangle className="w-3 h-3" />
                {active.length} active now
              </span>
            )}
          </div>

          {active.length > 0 && (
            <ul className="space-y-1 mb-2">
              {active.map((r) => (
                <li key={r.id} className="text-[12px] text-amber-900" data-testid={`admin-dashboard-bav-active-${r.id}`}>
                  <span className="font-semibold">{r.name}</span> · {r.start_date} → {r.end_date} · packages{" "}
                  {(r.package_codes || []).map(codeChip)} · available again <span className="font-semibold">{r.available_again_date}</span>
                </li>
              ))}
            </ul>
          )}

          {upcoming.length > 0 && (
            <div className="text-[12px] text-amber-800">
              <span className="font-semibold">Upcoming: </span>
              {upcoming.map((r, i) => (
                <span key={r.id}>
                  {r.name} ({r.start_date}){i < upcoming.length - 1 ? ", " : ""}
                </span>
              ))}
            </div>
          )}

          <Link
            to="/admin/settings/booking-availability"
            className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-amber-900 hover:text-amber-700 underline underline-offset-2"
            data-testid="admin-dashboard-bav-manage-link"
          >
            Manage rules <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
